<template>
  <div class="passes d-flex flex-column"
       :style="colorStyles"
  >
    <div v-if="imagePath" class="passes-image__wrapper">
      <image-component :path="imagePath"/>
    </div>
    <div class="passes_description__wrapper d-flex flex-column flex-grow-1">
      <div class="d-flex gap-1 align-items-center">
        <h5 class="m-0">{{ name }}</h5>
        <div v-if="isRecommended"
             class="passes_recommended"
        >
          {{ $t('Recommended') }}
        </div>
      </div>
      <div class="d-flex">
        <h6 class="passes_price">
          {{ channelSettings.currency }} <span>${{ item.base_price }}</span>{{ period }}
        </h6>
      </div>
      <div class="passes_description flex-grow-1">
        <div v-html="description"/>
      </div>
      <div v-if="type === $options.PASS_TYPES.PACKAGE && !isSinglePass">
        <div v-if="isParticipantSelect && !item.purchased">
          <div class="team-select">
            <select
                v-model="selectedParticipant"
                class="my-auto form-control"
                required
            >
              <option value selected disabled hidden>{{ $t('Select a Team') }}</option>
              <option
                  v-for="participant in participants"
                  :key="participant.id"
                  :value="participant.id"
                  :disabled="item.disabledParticipants?.includes(Number(participant.id))"
              >
                {{ participant.name }}
              </option>
            </select>
          </div>
        </div>
        <div v-else-if="isCategorySelect && !item.purchased"
            class="team-select"
          >
            <select
                v-model="selectedCategory"
                class="my-auto form-control"
                required
            >
              <option value="" selected disabled hidden>{{ $t('Select a Sport') }}</option>
              <option
                  v-for="(value, key) in item.event_categories"
                  :key="key"
                  :value="key"
                  :disabled="item.disabledCategories?.includes(Number(key))"
              >
                {{ value }}
              </option>
            </select>
      </div>
      </div>
      <div class="passes_button d-flex justify-content-center">
        <slot name="button">
          <app-link
              :to="linkTo"
              :disabled="item.purchased || isPurchaseDisabled"
          >
            {{ $t(buttonText) }}
          </app-link>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import {mapCacheActions} from 'vuex-cache';
import AppLink from '@/components/base-components/AppLink';
import {GET_ALL_PARTICIPANTS, GET_ORDERS} from '@/store/actionTypes';
import {BUY, SELECT_PACKAGE, UPCOMING} from '@/pages';
import {ORDER_TYPE, PACKAGE_TYPES, URL_QUERIES} from '@/const';
import ImageComponent from '@/components/base-components/ImageComponent.vue';
import Vue from 'vue';

export default {
  name: 'PassComponent',
  components: {
    ImageComponent,
    AppLink,
  },
  PASS_TYPES: {
    PACKAGE: ORDER_TYPE.package,
    SUBSCRIPTION: ORDER_TYPE.subscription,
    EVENT: ORDER_TYPE.event,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isRecommended: {
      default: true,
    },
    buttonText: {
      default: 'Get access',
    },
  },
  data() {
    return {
      selectedParticipant: '',
      selectedCategory: '',
    };
  },
  methods: {
    ...mapCacheActions({
      getParticipantsList: GET_ALL_PARTICIPANTS,
      getOrders: GET_ORDERS,
    }),
  },
  computed: {
    ...mapState({
      channelSettings: (state) => state.channel.settings,
      participants: (state) => state.participant.participantsList,
      domain: (state) => state.channel?.settings?.sub_domain,
    }),
    name() {
      const langObj = Object.fromEntries(
          Object.entries(this.item.name_lang || {})
              .map(([key, value]) => [key.toLowerCase(), value]),
      );
      return langObj?.[Vue.i18n.locale().toLowerCase()] || this.item.name;
    },
    isSinglePass() {
      return this.item.type === PACKAGE_TYPES.SINGLE;
    },
    type() {
      return this.item.passType || ORDER_TYPE.package;
    },
    imagePath() {
      return this.item?.image;
    },
    linkTo() {
      if (this.item.type === PACKAGE_TYPES.SINGLE) {
        if (this.$route.name === SELECT_PACKAGE.name) {
          return {
            name: BUY.name,
            query: {
              [URL_QUERIES.SINGLE_PACKAGE_ID]: this.item.id,
              [URL_QUERIES.EVENT_ID]: this.$route?.params?.id,
            },
          };
        } else {
          return {
            name: UPCOMING.name,
            query: {
              [URL_QUERIES.PACKAGE_ID]: this.item.id,
            },
          };
        }
      }
      const link = {
        [`${this.type}_id`]: this.item.id,
      };
      if (this.selectedParticipant) {
        link.participant_id = this.selectedParticipant;
      }
      if (this.selectedCategory) {
        link.event_category_id = this.selectedCategory;
      }
      return {
        name: BUY.name,
        query: link,
      };
    },
    period() {
      if (!this.item.offer) return '';
      const periodCount = this.item.period > 1 ? this.item.period : '';
      return `/${periodCount}${this.item.offer}`;
    },
    description() {
      const description = this.item.description_lang?.[Vue.i18n.locale()] || this.item.description;
      return this.convertEditorJsonToHtml(description) || description;
    },
    isParticipantSelect() {
      return Boolean(this.item.is_team_pass && this.participants.length);
    },
    isCategorySelect() {
      return Object.keys(this.item.event_categories || {})?.length > 1;
    },
    isPurchaseDisabled() {
      if (this.isSinglePass) return false;
      if (this.item.is_team_pass) return !this.selectedParticipant;
      if (this.isCategorySelect) return !this.selectedCategory;
      return false;
    },
    colorStyles() {
      return {
        '--background': this.item['package-background'],
        '--color': this.item['package-text-color'],
        '--button-background': this.item['package-button-background'],
        '--button-color': this.item['package-button-text-color'],
      };
    },
  },
  mounted() {
    if (this.item.is_team_pass) {
      !this.participants.length && this.getParticipantsList();
    }
  },
};
</script>

